<template>
  <div class="member-project-rates-table">
    <p class="text-default mb-15">
      Project rates take priority: if you track time to a project, the project rate is used. If not, the member rate is
      applied.
    </p>

    <div class="d-flex align-items-center border-info rounded-4 mb-15 gap-5 px-10 py-5">
      <i class="hi hi-info-filled hi-primary" />
      <span>{{ orgRatesBannerMessage }}</span>
    </div>

    <AppTable
      :table-data="projectRatesData"
      :loading="projectRatesLoading"
      table-classes="table-striped"
      hoverable
    >
      <template #empty>
        <div class="text-muted mt-10">The member doesn't have any project-specific rates.</div>
      </template>
      <AppTableColumn
        v-slot="{ row }"
        header="Client"
      >
        <div
          v-if="row.client_name"
          class="d-flex align-items-center"
        >
          <AppAvatarInitial
            :text="row.client_name"
            size="medium"
          />
          <a
            :href="row.clients_index_url"
            target="_blank"
            rel="noopener noreferrer"
            class="is-bold name-cell"
            :title="row.client_name"
          >{{ row.client_name }}</a>
        </div>
        <span v-else>—</span>
      </AppTableColumn>
      <AppTableColumn
        v-slot="{ row }"
        header="Project"
      >
        <div class="d-flex align-items-center">
          <AppAvatarInitial
            :text="row.project_name"
            size="medium"
          />
          <a
            :href="row.project_url"
            target="_blank"
            rel="noopener noreferrer"
            class="is-bold name-cell"
            :title="row.project_name"
          >{{ row.project_name }}</a>
        </div>
      </AppTableColumn>
      <AppTableColumn
        v-slot="{ row }"
        header="Role"
      >
        {{ row.role_name }}
      </AppTableColumn>
      <AppTableColumn
        v-slot="{ row }"
        header="Project pay rate / bill rate"
      >
        <div
          class="d-flex align-items-center"
          style="gap: 8px"
        >
          <span>{{ formatProjectRate(row.pay_rate, row.inherited_project_pay_rate, 'pay') }} /
            {{ formatProjectRate(row.bill_rate, row.inherited_project_bill_rate, 'bill') }}</span>
          <a
            v-if="row.can_edit"
            href="#"
            class="text-default"
            :aria-label="`Edit rate for ${row.project_name}`"
            @click.prevent="openEditDialog(row.manage_pay_rate_url)"
          >
            <i class="hi hi-edit" />
          </a>
        </div>
      </AppTableColumn>
    </AppTable>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import AppAvatarInitial from '~/components/AppAvatarInitial.vue';
import AppTable from '~/components/AppTable.vue';
import AppTableColumn from '~/components/AppTableColumn.vue';

export default {
  name: 'MembersProjectRatesTable',
  components: {
    AppAvatarInitial,
    AppTable,
    AppTableColumn,
  },
  props: {
    savedPayRate: {
      type: [Number, String],
      default: null,
    },
    savedBillRate: {
      type: [Number, String],
      default: null,
    },
  },
  computed: {
    ...mapState(['membership', 'organization']),
    ...mapGetters(['projectRatesData', 'projectRatesLoading']),
    organizationCurrency() {
      return this.organization.currency.currency;
    },
    orgRatesBannerMessage() {
      const payRate = this.formatRate(this.savedPayRate, 'pay');
      const billRate = this.formatRate(this.savedBillRate, 'bill');

      return `Member's current Pay and Bill rates are ${payRate} / ${billRate}`;
    },
  },
  created() {
    this.loadProjectRates(this.membership.id);
  },
  mounted() {
    window.AjaxDialog?.registerHook('response', 'projectRatesReload', ({ action }) => {
      if (action === 'close') {
        this.loadProjectRates(this.membership.id);
      }
    });
  },
  methods: {
    ...mapActions(['loadProjectRates']),
    formatRate(rate, type) {
      if (rate == null) { return type === 'pay' ? 'No pay rate' : 'No bill rate'; }

      return Number(rate).formatMoney(this.organizationCurrency);
    },
    formatProjectRate(rate, inherited, type) {
      const formatted = this.formatRate(rate, type);

      return inherited && rate != null ? `${formatted} (inherited)` : formatted;
    },
    openEditDialog(url) {
      window.AjaxDialog?.run(url);
    },
  },
};
</script>
